import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Zap } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { REGIONS, PRODUCTS } from "../../lib/data";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const ROICalculator = () => {
  const { t, lang } = useLang();
  const [regionId, setRegionId] = useState("gh");
  const [bill, setBill] = useState(1200);
  const [outage, setOutage] = useState(20);

  const region = REGIONS.find((r) => r.id === regionId) || REGIONS[0];

  const { annualSavings, paybackYears, product } = useMemo(() => {
    const monthlyBill = Number(bill) || 0;
    // diesel offset during outages (avg 2kW load, diesel ~3x grid tariff)
    const dieselPerKwhLocal = region.tariff * 3 * region.fx;
    const dieselMonthly = outage * 4.33 * 2 * dieselPerKwhLocal;
    const savedMonthly = 0.7 * monthlyBill + dieselMonthly;
    const annual = savedMonthly * 12;
    // recommend a real Gesung model based on the customer's monthly spend (converted to USD)
    const monthlyUsd = region.fx > 0 ? monthlyBill / region.fx : 0;
    const rec = PRODUCTS.find((p) => monthlyUsd <= p.maxUsd) || PRODUCTS[PRODUCTS.length - 1];
    const systemCostLocal = rec.usd * region.fx;
    const payback = annual > 0 ? systemCostLocal / annual : 0;
    return { annualSavings: annual, paybackYears: payback, product: rec };
  }, [bill, outage, region]);

  const fmt = (n) =>
    `${region.symbol}${Math.round(n).toLocaleString()}`;

  return (
    <section id="calculator" className="relative z-10 py-24 md:py-32 border-t border-white/10" data-testid="roi-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <SectionKicker>{t.roi.kicker}</SectionKicker>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-2xl leading-[0.95]">
            {t.roi.title}
          </h2>
          <p className="mt-5 text-white/60 max-w-xl">{t.roi.sub}</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {/* Inputs */}
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-[#181818] p-7 md:p-10 space-y-9">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">{t.roi.region}</label>
                <Select value={regionId} onValueChange={setRegionId}>
                  <SelectTrigger data-testid="roi-region" className="mt-3 bg-charcoal border-white/15 h-12 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`roi-region-${r.id}`}>
                        {lang === "fr" ? r.labelFR : r.labelEN}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">
                  {t.roi.bill} ({region.currency})
                </label>
                <div className="mt-3 flex items-center rounded-lg border border-white/15 bg-charcoal px-4">
                  <span className="text-white/40 mr-1">{region.symbol}</span>
                  <Input
                    type="number"
                    value={bill}
                    onChange={(e) => setBill(e.target.value)}
                    data-testid="roi-bill"
                    className="border-0 bg-transparent h-12 text-lg focus-visible:ring-0 px-1"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.2em] text-white/50">{t.roi.outage}</label>
                  <span className="font-display text-xl font-bold text-lime2">{outage}h</span>
                </div>
                <Slider
                  value={[outage]}
                  onValueChange={(v) => setOutage(v[0])}
                  min={0}
                  max={80}
                  step={1}
                  data-testid="roi-outage"
                  className="mt-5"
                />
              </div>
            </div>
          </Reveal>

          {/* Result */}
          <Reveal delay={0.12}>
            <div className="relative rounded-2xl overflow-hidden border border-emerald2/40 bg-gradient-to-br from-emerald2/20 via-charcoal to-charcoal p-8 md:p-10 h-full flex flex-col justify-between">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald2/20 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 text-emerald2 text-sm">
                  <TrendingUp size={18} /> {t.roi.resultLabel}
                </div>
                <motion.p
                  key={annualSavings}
                  initial={{ opacity: 0.4, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="font-display font-extrabold tracking-tighter text-5xl md:text-7xl mt-4 text-offwhite"
                  data-testid="roi-savings"
                >
                  {fmt(annualSavings)}
                </motion.p>
                <p className="text-white/40 text-sm mt-1">/ {t.roi.years === "years" ? "year" : "an"}</p>

                <div className="mt-8 flex items-center gap-3">
                  <span className="text-white/50 text-sm">{t.roi.payback}:</span>
                  <span className="font-display text-2xl font-bold text-lime2" data-testid="roi-payback">
                    {paybackYears > 0 ? paybackYears.toFixed(1) : "—"} {t.roi.years}
                  </span>
                </div>

                <div className="mt-5 rounded-lg border border-white/10 bg-black/20 p-4" data-testid="roi-product">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Recommended system</p>
                  <div className="flex items-baseline justify-between mt-1.5 gap-3">
                    <span className="font-display text-xl font-bold text-offwhite">Gesung {product.model}</span>
                    <span className="font-display text-lg font-bold text-lime2">{fmt(product.usd * region.fx)}</span>
                  </div>
                  <p className="text-white/45 text-xs mt-1">{product.use} · ${product.usd.toLocaleString()} USD</p>
                </div>
              </div>

              <div className="relative mt-10">
                <p className="text-white/40 text-xs leading-relaxed mb-5">{t.roi.note}</p>
                <button
                  onClick={() => scrollTo("contact")}
                  data-testid="roi-cta"
                  className="inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full px-6 py-3.5 transition-colors duration-300"
                >
                  <Zap size={16} /> {t.roi.cta}
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
